import React, { useEffect, useState, useRef } from "react";
import { DataGrid, gridClasses, GridMoreVertIcon } from "@mui/x-data-grid";
import {
  IconButton,
  MenuItem,
  Popover,
  Modal,
  Select,
  FormControl,
  Autocomplete,
  TextField,
  Checkbox,
  ListItemText,
} from "@mui/material";
import MatricIdsListModal from "../../components/Model/MatricIdsListModal";
import { CiEdit } from "react-icons/ci";
import { RxPlus, RxCross1 } from "react-icons/rx";
import { TbAlertHexagonFilled } from "react-icons/tb";
import { MdTransitEnterexit, MdClose, MdPhoneAndroid } from "react-icons/md";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { HttpService } from "../../service/HttpService";
import { toast } from "react-toastify";
import FullScreenLoader from "../../components/Loader/FullScreenLoader";

import { GoTrash } from "react-icons/go";

const resolveTagValue = (inputVal) => {
  if (!inputVal) return "";
  const valStr = String(inputVal).trim();
  const prefix = "1A405030001A4510000";
  if (valStr === "" || valStr === prefix) return "";
  if (valStr.startsWith(prefix)) {
    const after = valStr.substring(prefix.length).replace(/\D/g, "");
    if (!after) return "";
    const padded = after.padStart(5, "0");
    return prefix + padded;
  } else {
    if (valStr.length === 24) return valStr;
    const cleanVal = valStr.replace(/\D/g, "");
    if (!cleanVal) return "";
    const padded = cleanVal.padStart(5, "0");
    return prefix + padded;
  }
};

const Trimming = () => {
  const httpService = new HttpService();

  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rooms, setRooms] = useState([]);
  const [cultivars, setCultivars] = useState([]);
  const [importTags, setImportTags] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuRowId, setMenuRowId] = useState(null);

  // Inline editing state
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({});

  // Move to Inventory modal state
  const [openInventoryModal, setOpenInventoryModal] = useState(false);
  const [selectedActionId, setSelectedActionId] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const selectedPkgForAction = packages.find((p) => p.id === selectedActionId);

  // Matric modal states
  const [matricModalOpen, setMatricModalOpen] = useState(false);
  const [selectedMatricRow, setSelectedMatricRow] = useState(null);

  // Move to App modal states
  const [employees, setEmployees] = useState([]);
  const [openAssignModal, setOpenAssignModal] = useState(false);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]);
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [assignStartDate, setAssignStartDate] = useState(dayjs());
  const [assignEndDate, setAssignEndDate] = useState(dayjs());
  const [selectedRowIds, setSelectedRowIds] = useState([]);

  const handleOpenMatricModal = (row) => {
    setSelectedMatricRow(row);
    setMatricModalOpen(true);
  };

  const editingIdRef = useRef(editingId);
  const editValuesRef = useRef(editValues);

  useEffect(() => {
    editingIdRef.current = editingId;
    editValuesRef.current = editValues;
  }, [editingId, editValues]);

  const fetchTrimmingData = async () => {
    setLoading(true);
    try {
      const res = await httpService.get("harvest-trimming");
      setPackages(res?.data || []);
    } catch (e) {
      console.error(e);
      setPackages([]);
    }
    setLoading(false);
  };

  const fetchImportTags = async () => {
    try {
      const res = await httpService.get("harvestimport?assigned=false");
      const list = res?.data?.data || res?.data || [];
      setImportTags(list);
    } catch (e) {
      console.error("Error fetching import tags:", e);
    }
  };

  const fetchEmployees = async () => {
    try {
      const res = await httpService.get("harvest-trimming/assigned-employees");
      setEmployees(res?.data?.data || res?.data || []);
    } catch (e) {
      console.error("Error fetching employees:", e);
    }
  };

  useEffect(() => {
    fetchTrimmingData();
    fetchImportTags();
    fetchEmployees();
  }, []);

  useEffect(() => {
    if (openAssignModal) {
      fetchEmployees();
    }
  }, [openAssignModal]);

  // Handle click outside to auto-save
  useEffect(() => {
    const handleClickOutside = (event) => {
      const currentId = editingIdRef.current;
      const currentValues = editValuesRef.current;

      const isPopover = !!event.target.closest(
        ".MuiPopover-root, .MuiDialog-root, .MuiPickersPopper-root, .MuiPickersLayout-root, .MuiAutocomplete-popper, .MuiPopper-root",
      );
      if (isPopover) return;
      if (!document.body.contains(event.target)) return;

      if (currentId && currentValues && Object.keys(currentValues).length > 0) {
        const isInsideRow = event.target.closest(
          `.MuiDataGrid-row[data-id="${currentId}"], input, textarea, select, .MuiSelect-select`,
        );
        if (!isInsideRow) {
          const isScrollbarClick =
            !!event.target.closest(".MuiDataGrid-virtualScroller") &&
            !event.target.closest(".MuiDataGrid-row");
          if (isScrollbarClick) return;

          const isEmpty =
            !currentValues.harvest_date &&
            !currentValues.cultivar_id &&
            !currentValues.room_id &&
            !currentValues.source_package_tag;
          if (isEmpty) {
            handleCancelRow();
          } else {
            handleSaveRow(currentValues);
          }
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleMenuOpen = (event, id) => {
    setAnchorEl(event.currentTarget);
    setMenuRowId(id);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuRowId(null);
  };

  const handleAddRow = () => {
    const newRow = {
      id: `new_${Date.now()}`,
      week_of_harvest: "",
      harvest_date: null,
      cultivar_id: "",
      cultivar_name: "",
      room_id: "",
      room_name: "",
      plants: "",
      source_package_tag: "1A405030001A4510000",
      child_package_tag: "1A405030001A4510000",
      bulk_smalls_trim: "",
      weight: "",
      waste: "",
      isNew: true,
    };
    setPackages([newRow, ...packages]);
    setEditingId(newRow.id);
    setEditValues(newRow);
  };

  const handleEditRow = async (row) => {
    setEditingId(row.id);
    setEditValues({
      ...row,
      source_package_tag: row.source_package_tag_name || "1A405030001A4510000",
      child_package_tag: row.child_package_tag_name || "1A405030001A4510000",
    });
    handleMenuClose();

    if (row.harvest_date) {
      try {
        const response = await httpService.get(
          `harvest/room-cultivar?harvestedDate=${dayjs(row.harvest_date).format("YYYY-MM-DD")}`,
        );
        if (response?.data) {
          setRooms(response.data.rooms || []);
          setCultivars(response.data.cultivars || []);
        } else {
          setRooms([]);
          setCultivars([]);
        }
      } catch (error) {
        console.error("Error fetching room/cultivar data:", error);
      }
    } else {
      setRooms([]);
      setCultivars([]);
    }
  };

  const handleCancelRow = () => {
    setPackages((prev) => prev.filter((p) => !String(p.id).startsWith("new_")));
    setEditingId(null);
    setEditValues({});
  };

  const handleSaveRow = async (valuesToSave) => {
    const currentValues = valuesToSave || editValues;
    const currentId = editingIdRef.current || editingId;
    if (!currentValues || Object.keys(currentValues).length === 0) return;

    const resolvedSourceTag = resolveTagValue(currentValues.source_package_tag);
    const resolvedChildTag = resolveTagValue(currentValues.child_package_tag);
    const isEmpty =
      !currentValues.harvest_date &&
      !currentValues.cultivar_id &&
      !currentValues.room_id &&
      !resolvedSourceTag;
    if (isEmpty) {
      handleCancelRow();
      return;
    }

    setLoading(true);
    try {
      const isNew = String(currentId).startsWith("new_");
      const payload = {
        week_of_harvest: parseInt(currentValues.week_of_harvest) || 0,
        harvest_date: currentValues.harvest_date
          ? dayjs(currentValues.harvest_date).format("YYYY-MM-DD")
          : null,
        cultivar_id: parseInt(currentValues.cultivar_id) || null,
        room_id: parseInt(currentValues.room_id) || null,
        plants: parseInt(currentValues.plants) || 0,
        totes: currentValues.totes || "",
        source_package_tag: resolvedSourceTag,
        child_package_tag: resolvedChildTag,
        bulk_smalls_trim: currentValues.bulk_smalls_trim || "",
        weight:
          currentValues.weight === "" ||
          currentValues.weight == null ||
          isNaN(parseFloat(currentValues.weight))
            ? null
            : parseFloat(currentValues.weight),
        waste:
          currentValues.waste === "" ||
          currentValues.waste == null ||
          isNaN(parseFloat(currentValues.waste))
            ? null
            : parseFloat(currentValues.waste),
      };

      if (isNew) {
        await httpService.post("harvest-trimming", payload);
        toast.success("Package added successfully", { position: "top-right" });
      } else {
        await httpService.put(`harvest-trimming/${currentId}`, payload);
        toast.success("Package updated successfully", {
          position: "top-right",
        });
      }
      setEditingId(null);
      setEditValues({});
      fetchTrimmingData();
      fetchImportTags();
    } catch (error) {
      console.error(error);
      const isNew = String(currentId).startsWith("new_");
      const errMsg =
        error?.response?.data?.message ||
        (isNew ? "Failed to save package" : "Failed to update package");
      toast.error(errMsg, { position: "top-right" });
    }
    setLoading(false);
  };

  const handleFieldChange = async (field, value) => {
    let updatedValues = { ...editValues, [field]: value };

    if (field === "harvest_date" && value) {
      const getWeekOfYear = (date) => {
        const d = new Date(date);
        const yearStart = new Date(d.getFullYear(), 0, 1);
        return Math.ceil(
          ((d - yearStart) / 86400000 + yearStart.getDay() + 1) / 7,
        );
      };
      const weekNumber = getWeekOfYear(value);
      updatedValues = { ...updatedValues, week_of_harvest: weekNumber };

      try {
        const response = await httpService.get(
          `harvest/room-cultivar?harvestedDate=${value}`,
        );
        if (response?.data) {
          setRooms(response.data.rooms || []);
          setCultivars(response.data.cultivars || []);
          updatedValues = {
            ...updatedValues,
            room_id: "",
            cultivar_id: "",
            plants: "",
          };
        }
      } catch (error) {
        console.error("Error fetching room/cultivar data:", error);
        setRooms([]);
        setCultivars([]);
      }
    }

    if (
      (field === "cultivar_id" || field === "room_id") &&
      updatedValues.cultivar_id &&
      updatedValues.room_id &&
      updatedValues.harvest_date
    ) {
      try {
        const response = await httpService.get(
          `harvest/plant-count?plantId=${updatedValues.cultivar_id}&roomId=${updatedValues.room_id}&harvestedDate=${updatedValues.harvest_date}`,
        );
        const count =
          response?.data?.count ?? response?.data?.data ?? response?.data;
        if (count !== undefined && count !== null) {
          updatedValues = { ...updatedValues, plants: parseInt(count) || 0 };
        }
      } catch (error) {
        console.error("Error fetching plant count:", error);
      }
    }

    setEditValues(updatedValues);
  };
  const handleDuplicateRow = async (row) => {
    const newRow = {
      id: `new_${Date.now()}`,
      week_of_harvest: row.week_of_harvest || "",
      harvest_date: row.harvest_date
        ? dayjs(row.harvest_date).format("YYYY-MM-DD")
        : null,
      cultivar_id: row.cultivar_id || "",
      cultivar_name: row.cultivar_name || "",
      room_id: row.room_id || "",
      room_name: row.room_name || "",
      plants: row.plants || "",
      source_package_tag: row.source_package_tag || "",
      source_package_tag_name: row.source_package_tag_name || "",
      child_package_tag: "",
      child_package_tag_name: "",
      bulk_smalls_trim: row.bulk_smalls_trim || "",
      weight: row.weight || "",
      waste: row.waste || "",
      isNew: true,
    };

    setPackages([newRow, ...packages]);
    setEditingId(newRow.id);
    setEditValues(newRow);

    if (row.harvest_date) {
      try {
        const response = await httpService.get(
          `harvest/room-cultivar?harvestedDate=${dayjs(row.harvest_date).format("YYYY-MM-DD")}`,
        );
        if (response?.data) {
          setRooms(response.data.rooms || []);
          setCultivars(response.data.cultivars || []);
        } else {
          setRooms([]);
          setCultivars([]);
        }
      } catch (error) {
        console.error("Error fetching room/cultivar data:", error);
      }
    }
  };

  const handleMoveToInventory = (id) => {
    setSelectedActionId(id);
    setOpenInventoryModal(true);
    handleMenuClose();
  };

  const confirmMoveToInventory = async () => {
    setLoading(true);
    try {
      const pkg = packages.find((p) => p.id === selectedActionId);
      if (pkg) {
        const payload = {
          week_of_harvest: parseInt(pkg.week_of_harvest) || 0,
          harvest_date: pkg.harvest_date
            ? dayjs(pkg.harvest_date).format("YYYY-MM-DD")
            : null,
          cultivar_id: parseInt(pkg.cultivar_id) || null,
          room_id: parseInt(pkg.room_id) || null,
          plants: parseInt(pkg.plants) || 0,
          totes: pkg.totes || "",
          source_package_tag: parseInt(pkg.source_package_tag) || null,
          child_package_tag: parseInt(pkg.child_package_tag) || null,
          bulk_smalls_trim: pkg.bulk_smalls_trim || "",
          weight:
            pkg.weight === "" ||
            pkg.weight == null ||
            isNaN(parseFloat(pkg.weight))
              ? null
              : parseFloat(pkg.weight),
          location: "Trim Room",
          waste:
            pkg.waste === "" ||
            pkg.waste == null ||
            isNaN(parseFloat(pkg.waste))
              ? null
              : parseFloat(pkg.waste),
        };
        await httpService.post("harvest-inventory", payload);
        await httpService.put(`harvest-trimming/archive/${pkg.id}`);
        toast.success("Moved package to Inventory successfully", {
          position: "top-right",
        });
        fetchTrimmingData();
        fetchImportTags();
      }
    } catch (e) {
      console.error(e);
      toast.error("Failed to move package to Inventory", {
        position: "top-right",
      });
    }
    setLoading(false);
    setOpenInventoryModal(false);
    setSelectedActionId(null);
  };

  const handleMoveToApp = (id) => {
    setSelectedActionId(id || null);
    setSelectedRowIds(id ? [id] : []);
    setSelectedEmployeeIds([]);
    setEmployeeSearch("");
    setAssignStartDate(dayjs());
    setAssignEndDate(dayjs());
    fetchEmployees();
    setOpenAssignModal(true);
    handleMenuClose();
  };

  const confirmAssignEmployee = async () => {
    if (!selectedEmployeeIds || selectedEmployeeIds.length === 0) {
      toast.error("Please select at least one employee name", {
        position: "top-right",
      });
      return;
    }
    if (!assignStartDate || !assignEndDate) {
      toast.error("Please select both Start and End Dates", {
        position: "top-right",
      });
      return;
    }
    if (selectedRowIds.length === 0) {
      toast.error("Please select at least one package to assign", {
        position: "top-right",
      });
      return;
    }

    setLoading(true);
    try {
      const trimmingRows = selectedRowIds.map((id, index) => ({
        id: parseInt(id),
        priority: index + 1,
      }));

      const payload = {
        employee_id: selectedEmployeeIds.map((id) => parseInt(id)),
        start_date: assignStartDate.format("YYYY-MM-DD"),
        end_date: assignEndDate.format("YYYY-MM-DD"),
        trimming_rows: trimmingRows,
      };

      await httpService.post("harvest-trimming/assign-employee", payload);

      toast.success("Trimming packages assigned successfully", {
        position: "top-right",
      });
      setOpenAssignModal(false);
      setSelectedRowIds([]);
      setSelectedEmployeeIds([]);
      setEmployeeSearch("");
      fetchTrimmingData();
    } catch (error) {
      console.error(error);
      toast.error(error?.message || "Failed to assign employee to trimming", {
        position: "top-right",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRow = async () => {
    if (!selectedActionId) return;
    setLoading(true);
    try {
      await httpService.delete(`harvest-trimming/delete/${selectedActionId}`);
      toast.success("Package deleted successfully", { position: "top-right" });
      setDeleteModalOpen(false);
      setSelectedActionId(null);
      fetchTrimmingData();
      fetchImportTags();
    } catch (error) {
      toast.error("Failed to delete package", { position: "top-right" });
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      field: "action",
      headerName: "Action",
      width: 100,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <>
          {editingId === params.row.id ? (
            <IconButton onClick={handleCancelRow} color="error">
              <MdClose />
            </IconButton>
          ) : (
            <>
              <IconButton onClick={(e) => handleMenuOpen(e, params.row.id)}>
                <GridMoreVertIcon />
              </IconButton>
              {anchorEl && menuRowId === params.row.id && (
                <Popover
                  className="popup-menu"
                  open={Boolean(anchorEl)}
                  anchorEl={anchorEl}
                  onClose={handleMenuClose}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                >
                  <MenuItem
                    onClick={() => handleEditRow(params.row)}
                    className="menu-list-items"
                  >
                    <div className="menu-list-items-text">Edit</div>
                    <CiEdit style={{ cursor: "pointer" }} />
                  </MenuItem>
                  <MenuItem
                    onClick={() => handleMoveToInventory(params.row.id)}
                    className="menu-list-items"
                    // disabled={
                    //   !(
                    //     params.row.job_completed === 1 ||
                    //     params.row.job_completed === 2
                    //   )
                    // }
                  >
                    <div className="menu-list-items-text">
                      Move To Inventory
                    </div>
                    <MdTransitEnterexit style={{ cursor: "pointer" }} />
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      setSelectedActionId(params.row.id);
                      setDeleteModalOpen(true);
                      handleMenuClose();
                    }}
                    className="menu-list-items"
                  >
                    <div className="menu-list-items-text">Delete</div>
                    <GoTrash style={{ cursor: "pointer" }} />
                  </MenuItem>
                </Popover>
              )}
            </>
          )}
        </>
      ),
    },
    {
      field: "week_of_harvest",
      headerName: "Week of Harvest",
      flex: 1,
      minWidth: 150,
      renderCell: (params) =>
        editingId === params.row.id ? (
          <input
            type="number"
            value={editValues.week_of_harvest || ""}
            onChange={(e) =>
              handleFieldChange("week_of_harvest", e.target.value)
            }
            style={{
              width: "100%",
              padding: "8px",
              border: "1px solid #ccc",
              borderRadius: "4px",
            }}
          />
        ) : (
          params.value || "-"
        ),
    },
    {
      field: "harvest_date",
      headerName: "Harvest Date",
      flex: 1,
      minWidth: 200,
      renderCell: (params) =>
        editingId === params.row.id ? (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              value={
                editValues.harvest_date ? dayjs(editValues.harvest_date) : null
              }
              onChange={(newValue) => {
                const dateValue = newValue ? newValue.format("YYYY-MM-DD") : "";
                handleFieldChange("harvest_date", dateValue);
              }}
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                },
              }}
              disableFuture
            />
          </LocalizationProvider>
        ) : params.value ? (
          dayjs(params.value).format("MM-DD-YYYY")
        ) : (
          "-"
        ),
    },
    {
      field: "cultivar_name",
      headerName: "Cultivar",
      flex: 1,
      minWidth: 180,
      renderCell: (params) =>
        editingId === params.row.id ? (
          <FormControl fullWidth size="small">
            <Select
              value={editValues.cultivar_id || ""}
              onChange={(e) => handleFieldChange("cultivar_id", e.target.value)}
              native
            >
              <option value="">Select Cultivar</option>
              {cultivars.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </FormControl>
        ) : (
          params.value || "-"
        ),
    },
    {
      field: "room_name",
      headerName: "Room",
      flex: 1,
      minWidth: 180,
      renderCell: (params) =>
        editingId === params.row.id ? (
          <FormControl fullWidth size="small">
            <Select
              value={editValues.room_id || ""}
              onChange={(e) => handleFieldChange("room_id", e.target.value)}
              native
            >
              <option value="">Select Room</option>
              {rooms.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </Select>
          </FormControl>
        ) : (
          params.value || "-"
        ),
    },
    {
      field: "plants",
      headerName: "Plants",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <span
          style={{
            textDecoration: "underline",
            color: "#427fcfff",
            cursor: "pointer",
          }}
          onClick={() => handleOpenMatricModal(params.row)}
        >
          {params.value || 0}
        </span>
      ),
    },
    {
      field: "totes",
      headerName: "Totes",
      flex: 1,
      minWidth: 150,
      renderCell: (params) =>
        editingId === params.row.id ? (
          <input
            type="text"
            value={editValues.totes || ""}
            onChange={(e) => handleFieldChange("totes", e.target.value)}
            style={{
              width: "100%",
              padding: "8px",
              border: "1px solid #ccc",
              borderRadius: "4px",
            }}
          />
        ) : (
          params.value || "-"
        ),
    },
    {
      field: "source_package_tag",
      headerName: "Source Package Tag",
      flex: 1,
      minWidth: 350,
      renderCell: (params) => {
        const prefix = "1A405030001A4510000";
        const val = editValues.source_package_tag || "";
        let afterPrefix = "";
        if (val.startsWith(prefix)) {
          afterPrefix = val.substring(prefix.length).replace(/\D/g, "");
        } else {
          afterPrefix = val.replace(/\D/g, "").substring(0, 5);
        }
        return editingId === params.row.id ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              gap: "4px",
              padding: "4px 0",
            }}
          >
            <input
              type="text"
              placeholder="Last 5 digits or full tag"
              value={editValues.source_package_tag || ""}
              onChange={(e) => {
                let val = e.target.value;
                const prefix = "1A405030001A4510000";
                if (!val.startsWith(prefix)) {
                  val = prefix;
                }
                let afterPrefix = val
                  .substring(prefix.length)
                  .replace(/\D/g, "");
                if (afterPrefix.length > 5) {
                  afterPrefix = afterPrefix.substring(0, 5);
                }
                val = prefix + afterPrefix;
                setEditValues((prev) => ({
                  ...prev,
                  source_package_tag: val,
                  source_package_tag_name: val,
                }));
              }}
              style={{
                width: "100%",
                padding: "6px 8px",
                border: "1px solid #ccc",
                borderRadius: "4px",
                boxSizing: "border-box",
              }}
            />
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                fontSize: "11px",
                color: "#666",
              }}
            >
              <span style={{ fontFamily: "monospace", opacity: 0.8 }}>
                Prefix: {prefix}
              </span>
              <div style={{ display: "flex", gap: "4px" }}>
                {[0, 1, 2, 3, 4].map((index) => {
                  const char = afterPrefix[index];
                  return (
                    <span
                      key={index}
                      style={{
                        display: "inline-block",
                        width: "12px",
                        textAlign: "center",
                        borderBottom: char
                          ? "2px solid #00A272"
                          : "2px solid #c4c4c4",
                        color: char ? "#00A272" : "#a1a2a1",
                        fontFamily: "monospace",
                        fontWeight: "bold",
                        fontSize: "12px",
                      }}
                    >
                      {char || "_"}
                    </span>
                  );
                })}
              </div>
            </div>
          </div>
        ) : (
          params.row.source_package_tag_name || params.value || "-"
        );
      },
    },
    // {
    //   field: "child_package_tag",
    //   headerName: "Child Package Tag",
    //   flex: 1,
    //   minWidth: 350,
    //   renderCell: (params) => {
    //     const prefix = "1A405030001A4510000";
    //     const val = editValues.child_package_tag || "";
    //     let afterPrefix = "";
    //     if (val.startsWith(prefix)) {
    //       afterPrefix = val.substring(prefix.length).replace(/\D/g, "");
    //     } else {
    //       afterPrefix = val.replace(/\D/g, "").substring(0, 5);
    //     }
    //     return editingId === params.row.id ? (
    //       <div
    //         style={{
    //           display: "flex",
    //           flexDirection: "column",
    //           width: "100%",
    //           gap: "4px",
    //           padding: "4px 0",
    //         }}
    //       >
    //         <input
    //           type="text"
    //           placeholder="Last 5 digits or full tag"
    //           value={editValues.child_package_tag || ""}
    //           onChange={(e) => {
    //             let val = e.target.value;
    //             const prefix = "1A405030001A4510000";
    //             if (!val.startsWith(prefix)) {
    //               val = prefix;
    //             }
    //             let afterPrefix = val
    //               .substring(prefix.length)
    //               .replace(/\D/g, "");
    //             if (afterPrefix.length > 5) {
    //               afterPrefix = afterPrefix.substring(0, 5);
    //             }
    //             val = prefix + afterPrefix;
    //             setEditValues((prev) => ({
    //               ...prev,
    //               child_package_tag: val,
    //               child_package_tag_name: val,
    //             }));
    //           }}
    //           style={{
    //             width: "100%",
    //             padding: "6px 8px",
    //             border: "1px solid #ccc",
    //             borderRadius: "4px",
    //             boxSizing: "border-box",
    //           }}
    //         />
    //         <div
    //           style={{
    //             display: "flex",
    //             alignItems: "center",
    //             gap: "6px",
    //             fontSize: "11px",
    //             color: "#666",
    //           }}
    //         >
    //           <span style={{ fontFamily: "monospace", opacity: 0.8 }}>
    //             Prefix: {prefix}
    //           </span>
    //           <div style={{ display: "flex", gap: "4px" }}>
    //             {[0, 1, 2, 3, 4].map((index) => {
    //               const char = afterPrefix[index];
    //               return (
    //                 <span
    //                   key={index}
    //                   style={{
    //                     display: "inline-block",
    //                     width: "12px",
    //                     textAlign: "center",
    //                     borderBottom: char
    //                       ? "2px solid #00A272"
    //                       : "2px solid #c4c4c4",
    //                     color: char ? "#00A272" : "#a1a2a1",
    //                     fontFamily: "monospace",
    //                     fontWeight: "bold",
    //                     fontSize: "12px",
    //                   }}
    //                 >
    //                   {char || "_"}
    //                 </span>
    //               );
    //             })}
    //           </div>
    //         </div>
    //       </div>
    //     ) : (
    //       <div
    //         style={{
    //           display: "flex",
    //           alignItems: "center",
    //           justifyContent: "space-between",
    //           width: "100%",
    //         }}
    //       >
    //         <span>
    //           {params.row.child_package_tag_name || params.value || "-"}
    //         </span>
    //         <IconButton
    //           size="small"
    //           onClick={() => handleDuplicateRow(params.row)}
    //           title="Duplicate package (excluding child tag)"
    //           sx={{
    //             backgroundColor: "#00A272",
    //             color: "#FFFFFF",
    //             borderRadius: "4px",
    //             width: "28px",
    //             height: "28px",
    //             padding: "4px",
    //             "&:hover": {
    //               backgroundColor: "#00875F",
    //             },
    //           }}
    //         >
    //           <RxPlus />
    //         </IconButton>
    //       </div>
    //     );
    //   },
    // },
    // {
    //   field: "bulk_smalls_trim",
    //   headerName: "Bulk/Smalls/Trim",
    //   flex: 1,
    //   minWidth: 180,
    //   renderCell: (params) =>
    //     editingId === params.row.id ? (
    //       <FormControl fullWidth size="small">
    //         <Select
    //           value={editValues.bulk_smalls_trim || ""}
    //           onChange={(e) =>
    //             handleFieldChange("bulk_smalls_trim", e.target.value)
    //           }
    //           native
    //         >
    //           <option value="">Select Type</option>
    //           <option value="Bulk">Bulk</option>
    //           <option value="Smalls">Smalls</option>
    //           <option value="Trim">Trim</option>
    //         </Select>
    //       </FormControl>
    //     ) : (
    //       params.value || "-"
    //     ),
    // },
    {
      field: "weight",
      headerName: "Weight",
      flex: 1,
      minWidth: 120,
      renderCell: (params) =>
        editingId === params.row.id ? (
          <input
            type="number"
            step="any"
            value={editValues.weight || ""}
            onChange={(e) => handleFieldChange("weight", e.target.value)}
            style={{
              width: "100%",
              padding: "8px",
              border: "1px solid #ccc",
              borderRadius: "4px",
            }}
          />
        ) : params.value ? (
          `${params.value} lbs`
        ) : (
          "-"
        ),
    },
    {
      field: "waste",
      headerName: "Waste",
      flex: 1,
      minWidth: 120,
      renderCell: (params) =>
        editingId === params.row.id ? (
          <input
            type="number"
            step="any"
            value={editValues.waste || ""}
            onChange={(e) => handleFieldChange("waste", e.target.value)}
            style={{
              width: "100%",
              padding: "8px",
              border: "1px solid #ccc",
              borderRadius: "4px",
            }}
          />
        ) : params.value ? (
          `${params.value} lbs`
        ) : (
          "-"
        ),
    },
    {
      field: "assign_to",
      headerName: "Assign to App",
      flex: 1,
      minWidth: 120,
      renderCell: (params) =>
        editingId === params.row.id ? (
          <input
            type="string"
            value={editValues.assign_to || ""}
            onChange={(e) => handleFieldChange("assign_to", e.target.value)}
            style={{
              width: "100%",
              padding: "8px",
              border: "1px solid #ccc",
              borderRadius: "4px",
            }}
          />
        ) : (
          // `${params.value}`
          <span
            style={{
              backgroundColor: params.row.schedule_task_id
                ? "#E6F6F1"
                : "#F3F4F6",
              color: params.row.schedule_task_id ? "#00A272" : "#4B5563",
              padding: "4px 8px",
              borderRadius: "4px",
              fontWeight: 600,
              fontSize: "12px",
              display: "inline-block",
            }}
          >
            {params.row.schedule_task_id ? "Yes" : "No"}
          </span>
        ),
    },
  ];

  const assignColumns = [
    {
      field: "selection",
      headerName: "Select & Priority",
      width: 130,
      sortable: false,
      renderCell: (params) => {
        const row = params.row;
        const isSelected = selectedRowIds.includes(row.id);
        const priorityIndex = selectedRowIds.indexOf(row.id);
        const displayPriority = priorityIndex !== -1 ? priorityIndex + 1 : null;

        const handleCheckboxChange = () => {
          if (isSelected) {
            setSelectedRowIds(selectedRowIds.filter((id) => id !== row.id));
          } else {
            setSelectedRowIds([...selectedRowIds, row.id]);
          }
        };

        return (
          <div style={{ display: "flex", alignItems: "center" }}>
            {isSelected ? (
              <div
                onClick={handleCheckboxChange}
                style={{
                  backgroundColor: "#00A272",
                  color: "#FFF",
                  borderRadius: "4px",
                  width: "22px",
                  height: "22px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: "600",
                  fontSize: "12px",
                  cursor: "pointer",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                }}
              >
                {displayPriority}
              </div>
            ) : (
              <div
                onClick={handleCheckboxChange}
                style={{
                  border: "2px solid #D1D5DB",
                  borderRadius: "4px",
                  width: "22px",
                  height: "22px",
                  cursor: "pointer",
                  backgroundColor: "#FFF",
                }}
              />
            )}
          </div>
        );
      },
    },
    {
      field: "week_of_harvest",
      headerName: "Week of Harvest",
      width: 130,
    },
    {
      field: "harvest_date",
      headerName: "Harvest Date",
      width: 140,
      renderCell: (params) =>
        params.value ? dayjs(params.value).format("MM-DD-YYYY") : "-",
    },
    {
      field: "cultivar_name",
      headerName: "Cultiver",
      width: 180,
    },
    {
      field: "room_name",
      headerName: "Room",
      width: 150,
    },
    {
      field: "plants",
      headerName: "Plants",
      width: 100,
    },
    {
      field: "source_package_tag_name",
      headerName: "Source Package Tag",
      width: 300,
    },
    {
      field: "child_package_tag_name",
      headerName: "Child Package Tag",
      width: 300,
    },
    {
      field: "bulk_smalls_trim",
      headerName: "Bulk/Smalls/Trim",
      width: 150,
    },
    {
      field: "weight",
      headerName: "Weight",
      width: 100,
    },
    {
      field: "waste",
      headerName: "Waste",
      width: 100,
    },
    {
      field: "schedule_task_id",
      headerName: "Assign to App",
      width: 130,
      renderCell: (params) => (
        <span
          style={{
            backgroundColor: params.value ? "#E6F6F1" : "#F3F4F6",
            color: params.value ? "#00A272" : "#4B5563",
            padding: "4px 8px",
            borderRadius: "4px",
            fontWeight: 600,
            fontSize: "12px",
            display: "inline-block",
          }}
        >
          {params.value ? "Yes" : "No"}
        </span>
      ),
    },
  ];

  const hasAssignablePackages = packages.some(
    (p) => !String(p.id).startsWith("new_") && !p.schedule_task_id,
  );

  return (
    <>
      {loading && <FullScreenLoader />}
      <div className="harvest">
        <div
          className="page-title"
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginBottom: "15px",
          }}
        >
          <div className="button-wrap">
            <button
              type="button"
              className="btn btn-primary"
              disabled={!hasAssignablePackages}
              onClick={() => handleMoveToApp()}
              style={{
                backgroundColor: !hasAssignablePackages ? "#ccc" : undefined,
                borderColor: !hasAssignablePackages ? "#ccc" : undefined,
                cursor: !hasAssignablePackages ? "not-allowed" : "pointer",
              }}
            >
              <MdPhoneAndroid /> Assign To App
            </button>
          </div>
        </div>

        <div className="frozen-table-wrapper" style={{ width: "100%" }}>
          {/* Fake frozen header */}
          <div className="fake-frozen-header">
            <div
              className={"fake-header-cell"}
              style={{
                width: 100,
                backgroundColor: "#e7e7ff",
                fontFamily: "inherit",
                justifyContent: "center",
              }}
            >
              Action
            </div>
          </div>
          <DataGrid
            rows={packages}
            columns={columns}
            autoHeight
            getRowHeight={(params) => (params.id === editingId ? 75 : null)}
            disableRowSelectionOnClick
            getRowClassName={(params) =>
              params.row.schedule_task_id ? "assigned-row" : ""
            }
            columnBuffer={columns.length}
            sx={{
              border: "1px solid #E0E0E0",
              [`& .${gridClasses.cell}`]: { py: 1 },
              "& .assigned-row": {
                backgroundColor: "#E6F6F1 !important",
              },
              "& .assigned-row .MuiDataGrid-cell[data-field='action']": {
                backgroundColor: "#E6F6F1 !important",
              },
              "& .assigned-row:hover": {
                backgroundColor: "#d5f0e7 !important",
              },
              "& .assigned-row:hover .MuiDataGrid-cell[data-field='action']": {
                backgroundColor: "#d5f0e7 !important",
              },
              "& .MuiDataGrid-cell[data-field='action']": {
                position: "sticky !important",
                left: "0 !important",
                zIndex: "2 !important",
                backgroundColor: "#fff !important",
                borderRight: "1px solid #E0E0E0 !important",
                boxShadow: "2px 0 5px -2px rgba(0, 0, 0, 0.2) !important",
              },
              "& .MuiDataGrid-row:not(.assigned-row):hover .MuiDataGrid-cell[data-field='action']":
                {
                  backgroundColor: "#F5F5F7 !important",
                },
              "& .MuiDataGrid-row.Mui-selected:not(.assigned-row) .MuiDataGrid-cell[data-field='action']":
                {
                  backgroundColor: "#E6F6F1 !important",
                },
            }}
            hideFooter
            showCellVerticalBorder
            showColumnVerticalBorder
          />
        </div>

        {/* Move to Inventory Modal */}
        <Modal
          open={openInventoryModal}
          onClose={() => setOpenInventoryModal(false)}
        >
          <div className="modal_block">
            <div className="modal_header">
              <div className="modal_title_wrapper">
                <h2 className="modal_title">Move to Inventory</h2>
              </div>
              <div className="cancel-btn-wrap">
                <RxCross1 onClick={() => setOpenInventoryModal(false)} />
              </div>
            </div>
            <div className="modal_content">
              <div className="modal_content_inner">
                <div>
                  <TbAlertHexagonFilled />
                </div>
                <div>
                  <p>
                    {selectedPkgForAction &&
                    !selectedPkgForAction.child_package_tag
                      ? "There is no Child Package Tag assigned to this record. Do you still want to move it to Inventory?"
                      : "Are you sure you want to move this package to Inventory?"}
                  </p>
                </div>
              </div>
            </div>
            <div className="modal_footer">
              <button
                onClick={() => setOpenInventoryModal(false)}
                className="btn btn-border"
              >
                {selectedPkgForAction && !selectedPkgForAction.child_package_tag
                  ? "Cancel"
                  : "No"}
              </button>
              <button
                onClick={confirmMoveToInventory}
                className="btn btn-primary"
              >
                {selectedPkgForAction && !selectedPkgForAction.child_package_tag
                  ? "Move"
                  : "Yes"}
              </button>
            </div>
          </div>
        </Modal>
        {/* Delete Modal */}
        <Modal open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
          <div className="modal_block">
            <div className="modal_header">
              <div className="modal_title_wrapper">
                <h2 className="modal_title">Are you Sure?</h2>
              </div>
              <div className="cancel-btn-wrap">
                <RxCross1 onClick={() => setDeleteModalOpen(false)} />
              </div>
            </div>
            <div className="modal_content">
              <div className="modal_content_inner">
                <div>
                  <TbAlertHexagonFilled />
                </div>
                <div>
                  <p>Do you want to delete?</p>
                </div>
              </div>
            </div>
            <div className="modal_footer">
              <button
                onClick={() => setDeleteModalOpen(false)}
                className="btn btn-border"
              >
                No
              </button>
              <button onClick={handleDeleteRow} className="btn btn-primary">
                Yes
              </button>
            </div>
          </div>
        </Modal>
        {/* Assign To Employee Modal */}
        <Modal open={openAssignModal} onClose={() => setOpenAssignModal(false)}>
          <div
            className="modal_block"
            style={{ width: "90%", maxWidth: 1000, boxSizing: "border-box" }}
          >
            <div className="modal_header">
              <div className="modal_title_wrapper">
                <h2 className="modal_title">Assign To Employee</h2>
              </div>
              <div className="cancel-btn-wrap">
                <RxCross1
                  onClick={() => setOpenAssignModal(false)}
                  style={{ cursor: "pointer" }}
                />
              </div>
            </div>
            <div className="modal_content" style={{ padding: "20px 24px" }}>
              {/* Employee Selection and Date Pickers */}
              <div className="row" style={{ marginBottom: "20px" }}>
                <div className="col-md-12" style={{ marginBottom: "15px" }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "6px",
                      fontWeight: 500,
                      fontSize: "14px",
                    }}
                  >
                    Employee
                  </label>
                  {(() => {
                    const filteredEmployees = employees.filter((emp) =>
                      emp.name
                        ?.toLowerCase()
                        .includes(employeeSearch.toLowerCase()),
                    );
                    const isAllFilteredSelected =
                      filteredEmployees.length > 0 &&
                      filteredEmployees.every((emp) =>
                        selectedEmployeeIds
                          .map(Number)
                          .includes(Number(emp.id)),
                      );
                    const filteredSelectedCount = filteredEmployees.filter(
                      (emp) =>
                        selectedEmployeeIds
                          .map(Number)
                          .includes(Number(emp.id)),
                    ).length;

                    return (
                      <FormControl fullWidth size="small">
                        <Select
                          multiple
                          value={selectedEmployeeIds}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val.includes("all")) {
                              const filteredIds = filteredEmployees.map((emp) =>
                                Number(emp.id),
                              );
                              if (isAllFilteredSelected) {
                                setSelectedEmployeeIds((prev) =>
                                  prev.filter(
                                    (id) => !filteredIds.includes(Number(id)),
                                  ),
                                );
                              } else {
                                const combined = Array.from(
                                  new Set([
                                    ...selectedEmployeeIds.map(Number),
                                    ...filteredIds,
                                  ]),
                                );
                                setSelectedEmployeeIds(combined);
                              }
                            } else {
                              setSelectedEmployeeIds(
                                typeof val === "string" ? val.split(",") : val,
                              );
                            }
                          }}
                          displayEmpty
                          renderValue={(selected) => {
                            if (!selected || selected.length === 0) {
                              return (
                                <span style={{ color: "#aaa" }}>
                                  Select Employee Name
                                </span>
                              );
                            }
                            const selectedNames = employees
                              .filter((emp) =>
                                selected.map(Number).includes(Number(emp.id)),
                              )
                              .map((emp) => emp.name);
                            return selectedNames.join(", ");
                          }}
                          MenuProps={{
                            PaperProps: {
                              style: {
                                maxHeight: 300,
                              },
                            },
                          }}
                          sx={{
                            backgroundColor: "#FFF",
                            borderRadius: "4px",
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#E0E0E0",
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#00A272 !important",
                            },
                          }}
                        >
                          <div
                            onMouseDown={(e) => e.stopPropagation()}
                            style={{
                              padding: "8px 12px",
                              position: "sticky",
                              top: 0,
                              backgroundColor: "#fff",
                              zIndex: 1,
                            }}
                          >
                            <TextField
                              placeholder="Search employees..."
                              size="small"
                              fullWidth
                              value={employeeSearch}
                              onChange={(e) =>
                                setEmployeeSearch(e.target.value)
                              }
                              onClick={(e) => e.stopPropagation()}
                              onKeyDown={(e) => e.stopPropagation()}
                              sx={{
                                "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                                  {
                                    borderColor: "#00A272 !important",
                                  },
                              }}
                            />
                          </div>
                          <MenuItem value="all">
                            <Checkbox
                              checked={isAllFilteredSelected}
                              indeterminate={
                                filteredSelectedCount > 0 &&
                                !isAllFilteredSelected
                              }
                              sx={{
                                color: "#00A272",
                                "&.Mui-checked": { color: "#00A272" },
                                "&.MuiCheckbox-indeterminate": {
                                  color: "#00A272",
                                },
                              }}
                            />
                            <ListItemText primary="Select All" />
                          </MenuItem>
                          {filteredEmployees.map((emp) => (
                            <MenuItem key={emp.id} value={emp.id}>
                              <Checkbox
                                checked={selectedEmployeeIds
                                  .map(Number)
                                  .includes(Number(emp.id))}
                                sx={{
                                  color: "#00A272",
                                  "&.Mui-checked": { color: "#00A272" },
                                }}
                              />
                              <ListItemText primary={emp.name} />
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    );
                  })()}
                </div>
              </div>
              <div
                className="row"
                style={{ marginBottom: "20px", display: "flex", gap: "15px" }}
              >
                <div style={{ flex: 1 }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "6px",
                      fontWeight: 500,
                      fontSize: "14px",
                    }}
                  >
                    Start Date
                  </label>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      value={assignStartDate}
                      onChange={(newValue) => setAssignStartDate(newValue)}
                      slotProps={{
                        textField: {
                          size: "small",
                          fullWidth: true,
                          sx: {
                            backgroundColor: "#FFF",
                            borderRadius: "4px",
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#E0E0E0",
                            },
                            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                              {
                                borderColor: "#00A272 !important",
                              },
                          },
                        },
                      }}
                    />
                  </LocalizationProvider>
                </div>
                <div style={{ flex: 1 }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "6px",
                      fontWeight: 500,
                      fontSize: "14px",
                    }}
                  >
                    End Date
                  </label>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      value={assignEndDate}
                      onChange={(newValue) => setAssignEndDate(newValue)}
                      slotProps={{
                        textField: {
                          size: "small",
                          fullWidth: true,
                          sx: {
                            backgroundColor: "#FFF",
                            borderRadius: "4px",
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#E0E0E0",
                            },
                            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                              {
                                borderColor: "#00A272 !important",
                              },
                          },
                        },
                      }}
                    />
                  </LocalizationProvider>
                </div>
              </div>

              {/* Table displaying Trimming Packages */}
              <div
                style={{
                  // height: 350,
                  width: "100%",
                  border: "1px solid #E0E0E0",
                  borderRadius: "4px",
                }}
              >
                <DataGrid
                  rows={packages.filter(
                    (p) =>
                      !String(p.id).startsWith("new_") && !p.schedule_task_id,
                  )}
                  columns={assignColumns}
                  disableRowSelectionOnClick
                  rowHeight={52}
                  columnBuffer={assignColumns.length}
                  sx={{
                    border: "none",
                    [`& .${gridClasses.cell}`]: { py: 1 },
                  }}
                  hideFooter
                  showCellVerticalBorder
                  showColumnVerticalBorder
                />
              </div>
            </div>
            <div
              className="modal_footer"
              style={{
                borderTop: "1px solid #F3F4F6",
                padding: "16px 24px",
                display: "flex",
                justifyContent: "flex-end",
                gap: "12px",
              }}
            >
              <button
                onClick={() => setOpenAssignModal(false)}
                className="btn btn-border"
                style={{
                  padding: "8px 20px",
                  borderRadius: "6px",
                  fontWeight: 500,
                  fontSize: "14px",
                  border: "1px solid #D1D5DB",
                  backgroundColor: "#FFF",
                  color: "#374151",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmAssignEmployee}
                className="btn btn-primary"
                style={{
                  padding: "8px 24px",
                  borderRadius: "6px",
                  fontWeight: 500,
                  fontSize: "14px",
                  backgroundColor: "#00A272",
                  border: "none",
                  color: "#FFF",
                  cursor: "pointer",
                }}
              >
                Save
              </button>
            </div>
          </div>
        </Modal>
        <MatricIdsListModal
          open={matricModalOpen}
          handleClose={() => {
            setMatricModalOpen(false);
            setSelectedMatricRow(null);
          }}
          row={selectedMatricRow}
        />
      </div>
    </>
  );
};

export default Trimming;
